import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, Project, Script, Room } from "@/types";
import { apiClient } from "@/services/api";

interface AppState {
  // Auth（v1.16：JWT 存 httpOnly Cookie，前端不存 token）
  user: User | null;
  isAuthenticated: boolean;
  authReady: boolean;

  // Current workspace
  currentProject: Project | null;
  currentScript: Script | null;
  currentRoom: Room | null;

  // UI State
  sidebarOpen: boolean;
  theme: "light" | "dark";

  // Actions
  setUser: (user: User | null) => void;
  bootstrap: () => Promise<void>;
  logout: () => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  setCurrentScript: (script: Script | null) => void;
  setCurrentRoom: (room: Room | null) => void;
  toggleSidebar: () => void;
  setTheme: (theme: "light" | "dark") => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      authReady: false,
      currentProject: null,
      currentScript: null,
      currentRoom: null,
      sidebarOpen: true,
      theme: "dark",

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      // 启动时用 Cookie 会话探测登录态
      bootstrap: async () => {
        if (get().authReady) return;
        const res = await apiClient.getMe();
        if (res.success && res.data) {
          set({ user: res.data, isAuthenticated: true, authReady: true });
        } else {
          set({ user: null, isAuthenticated: false, authReady: true });
        }
      },

      logout: async () => {
        await apiClient.logout();
        set({
          user: null,
          isAuthenticated: false,
          currentProject: null,
          currentScript: null,
          currentRoom: null,
        });
      },

      setCurrentProject: (project) => set({ currentProject: project }),
      setCurrentScript: (script) => set({ currentScript: script }),
      setCurrentRoom: (room) => set({ currentRoom: room }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: "voice-prompter-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        theme: state.theme,
        currentProject: state.currentProject,
        currentScript: state.currentScript,
        currentRoom: state.currentRoom,
      }),
    }
  )
);
